<script setup lang="ts">
import MegaMenu from './MegaMenu.vue'
import MegaMenuNavItems from './MegaMenuNavItems.vue'
import MegaMenuAccentedLink from './MegaMenuAccentedLink.vue'
import MegaMenuRegularLink from './MegaMenuRegularLink.vue'
import type { IMenuLinks } from './types'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import './style.css'
import type { LucideIcon } from 'lucide-vue-next'
import { useMediaQuery } from '@vueuse/core'
import {
  LucideLandmark,
  LucideBriefcaseBusiness,
  LucideChartColumnIncreasing,
  LucideBanknote,
  LucideCreditCard,
  LucideChartBarBig
} from 'lucide-vue-next'

const { getPathWithLocale } = useLocalizedPath()
const localizedCreditsIslamic = computed(() => getPathWithLocale('/credits/islamic'))

const props = defineProps<{ menuLinks: IMenuLinks }>()

// The menu is hidden by CSS up to 1180px, so the ad must not mount there and count a hidden impression.
// Keep in sync with the min-[1181px] classes here and in MobileSidebar.vue
const isDesktop = useMediaQuery('(min-width: 1181px)')

const iconMap: Record<string, LucideIcon> = {
  '/banks/branches': LucideLandmark,
  '/banks/atms': LucideBanknote,
  '/vacancies': LucideBriefcaseBusiness,
  '/rates': LucideChartColumnIncreasing,
  '/rates/nbkr': LucideChartColumnIncreasing,
  '/banks/statistics': LucideChartBarBig,
  '/map?t=bank&l=1&b=branch,savings_bank': LucideLandmark,
  '/map?t=bank&l=1&b=atm,terminal': LucideCreditCard
}
</script>

<template>
  <div class="ml-8 mr-4 hidden items-center gap-4 whitespace-nowrap min-[1181px]:flex xl:ml-20 xl:gap-5">
    <MegaMenu :trigger-name="$t('common.banks')">
      <template #header>
        <MegaMenuAccentedLink to="/banks">{{
          $t('common.banks_of_kyrgyzstan')
        }}</MegaMenuAccentedLink>
      </template>
      <MegaMenuNavItems v-for="(group, groupIndex) in props.menuLinks.banks" :key="groupIndex">
        <MegaMenuRegularLink
          v-for="(link, linkIndex) in group"
          :key="`${groupIndex}-${linkIndex}`"
          :to="link.url"
        >
          <component :is="iconMap[link.url] || LucideLandmark" class="size-6" />{{ $t(link.title) }}
        </MegaMenuRegularLink>
      </MegaMenuNavItems>
      <template #footer>
        <div class="border-t pt-4">
          <MegaMenuAccentedLink to="/bank/nbkr">{{ $t('menu.banks.nbkr') }}</MegaMenuAccentedLink>
        </div>
      </template>
      <template #banner>
        <div class="w-full">
          <WidgetReviveAd zoneid="23" />
        </div>
      </template>
    </MegaMenu>

    <MegaMenu :trigger-name="$t('common.credits')">
      <template #header>
        <MegaMenuAccentedLink to="/credits">{{ $t('menu.credits.title') }}</MegaMenuAccentedLink>
      </template>
      <MegaMenuNavItems v-for="(group, groupIndex) in props.menuLinks.credits" :key="groupIndex">
        <MegaMenuRegularLink
          v-for="(link, linkIndex) in group"
          :key="`${groupIndex}-${linkIndex}`"
          :to="link.url"
        >
          <component :is="link.icon" v-if="link.icon" class="size-4" />{{ $t(link.title) }}
        </MegaMenuRegularLink>
      </MegaMenuNavItems>
      <template #footer>
        <div class="border-t pt-4">
          <MegaMenuAccentedLink to="/credits/online-request">{{
            $t('menu.credits.online_request')
          }}</MegaMenuAccentedLink>
        </div>
      </template>
      <template #banner>
        <div class="w-full">
          <WidgetReviveAd zoneid="20" />
        </div>
      </template>
    </MegaMenu>

    <MegaMenu :trigger-name="$t('common.deposits')">
      <template #header>
        <MegaMenuAccentedLink to="/deposits">{{ $t('menu.deposits.title') }}</MegaMenuAccentedLink>
      </template>
      <MegaMenuNavItems v-for="(group, groupIndex) in props.menuLinks.deposits" :key="groupIndex">
        <MegaMenuRegularLink
          v-for="(link, linkIndex) in group"
          :key="`${groupIndex}-${linkIndex}`"
          :to="link.url"
        >
          <component :is="link.icon" v-if="link.icon" class="size-4" />{{ $t(link.title) }}
        </MegaMenuRegularLink>
      </MegaMenuNavItems>
      <template #footer>
        <div class="border-t pt-4">
          <MegaMenuAccentedLink to="/deposits/calculator">
            {{ $t('menu.deposits.calculator') }}
          </MegaMenuAccentedLink>
        </div>
      </template>
      <template #banner>
        <div class="w-full">
          <WidgetReviveAd zoneid="19" />
        </div>
      </template>
    </MegaMenu>

    <MegaMenu :trigger-name="$t('common.cards')">
      <template #header>
        <MegaMenuAccentedLink to="/cards">{{ $t('menu.cards.title') }}</MegaMenuAccentedLink>
      </template>
      <MegaMenuNavItems v-for="(group, groupIndex) in props.menuLinks.cards" :key="groupIndex">
        <MegaMenuRegularLink
          v-for="(link, linkIndex) in group"
          :key="`${groupIndex}-${linkIndex}`"
          :to="link.url"
        >
          <component :is="link.icon" v-if="link.icon" class="size-4" />{{ $t(link.title) }}
        </MegaMenuRegularLink>
      </MegaMenuNavItems>
      <template #banner>
        <div class="w-full">
          <WidgetReviveAd zoneid="21" />
        </div>
      </template>
    </MegaMenu>

    <NuxtLink :to="localizedCreditsIslamic" class="main-menu-item">{{
      $t('menu.triggers.islamic')
    }}</NuxtLink>

    <MegaMenu :trigger-name="$t('menu.triggers.more')">
      <MegaMenuNavItems v-for="(group, groupIndex) in props.menuLinks.other" :key="groupIndex">
        <MegaMenuRegularLink
          v-for="(link, linkIndex) in group"
          :key="`${groupIndex}-${linkIndex}`"
          :to="link.url"
        >
          {{ $t(link.title) }}
        </MegaMenuRegularLink>
      </MegaMenuNavItems>
      <template #banner>
        <div class="w-full">
          <WidgetReviveAd zoneid="22" />
        </div>
      </template>
    </MegaMenu>

    <ClientOnly>
      <WidgetReviveAd v-if="isDesktop" zoneid="17" />
    </ClientOnly>
  </div>
</template>
